import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type ServiceRequestKind =
  | 'equipment_issue'
  | 'refill'
  | 'scheduling'
  | 'other';

export type ServiceRequestStatus =
  | 'open'
  | 'in_progress'
  | 'resolved'
  | 'cancelled';

export interface ServiceRequest {
  id: string;
  customerId: string;
  locationId: string | null;
  createdBy: string | null;
  kind: ServiceRequestKind;
  subject: string;
  body: string | null;
  status: ServiceRequestStatus;
  sfTicketId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ServiceRequestRow {
  id: string;
  customer_id: string;
  location_id: string | null;
  created_by: string | null;
  kind: ServiceRequestKind;
  subject: string;
  body: string | null;
  status: ServiceRequestStatus;
  sf_ticket_id: string | null;
  created_at: string;
  updated_at: string;
}

function rowToServiceRequest(row: ServiceRequestRow): ServiceRequest {
  return {
    id: row.id,
    customerId: row.customer_id,
    locationId: row.location_id,
    createdBy: row.created_by,
    kind: row.kind,
    subject: row.subject,
    body: row.body,
    status: row.status,
    sfTicketId: row.sf_ticket_id,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

const LIST_KEY = ['service-requests'] as const;
const OPEN_KEY = ['service-requests', 'open'] as const;

/**
 * useServiceRequests — list this customer's tickets. RLS already restricts
 * to the current customer; we additionally drop resolved + cancelled when
 * `onlyOpen` is true (dashboard default).
 */
export function useServiceRequests(opts: { onlyOpen?: boolean } = {}) {
  const onlyOpen = opts.onlyOpen ?? false;
  return useQuery<ServiceRequest[]>({
    queryKey: onlyOpen ? OPEN_KEY : LIST_KEY,
    queryFn: async (): Promise<ServiceRequest[]> => {
      let q = supabase
        .schema('orders')
        .from('service_requests')
        .select(
          'id, customer_id, location_id, created_by, kind, subject, body, status, sf_ticket_id, created_at, updated_at',
        )
        .order('created_at', { ascending: false });
      if (onlyOpen) q = q.in('status', ['open', 'in_progress']);
      const { data, error } = await q;
      if (error) throw error;
      return (data as ServiceRequestRow[] | null)?.map(rowToServiceRequest) ?? [];
    },
    staleTime: 30 * 1000,
  });
}

interface CreateInput {
  customerId: string;
  locationId: string | null;
  kind: ServiceRequestKind;
  subject: string;
  body?: string;
}

export function useCreateServiceRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      customerId,
      locationId,
      kind,
      subject,
      body,
    }: CreateInput) => {
      const trimmedSubject = subject.trim();
      if (!trimmedSubject) throw new Error('Subject is required.');

      // Route through the Netlify function so a notification email goes
      // out to service@brixbev.com on every ticket. The fn handles the
      // insert + the email; the customer's ticket lands either way (email
      // failure is best-effort).
      const { data: sess } = await supabase.auth.getSession();
      const token = sess.session?.access_token;
      if (!token) throw new Error('Not signed in.');

      const res = await fetch('/.netlify/functions/submit-service-request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          customer_id: customerId,
          location_id: locationId,
          kind,
          subject: trimmedSubject,
          body: body?.trim() ? body.trim() : null,
        }),
      });
      const json = (await res.json().catch(() => ({}))) as {
        ok?: boolean;
        error?: string;
        email_sent?: boolean;
        email_warning?: string | null;
      };
      if (!res.ok || !json.ok) {
        throw new Error(json.error ?? `HTTP ${res.status}`);
      }
      return { emailSent: !!json.email_sent, emailWarning: json.email_warning ?? null };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: OPEN_KEY });
    },
  });
}

export function kindLabel(kind: ServiceRequestKind): string {
  switch (kind) {
    case 'equipment_issue':
      return 'Equipment issue';
    case 'refill':
      return 'Refill';
    case 'scheduling':
      return 'Scheduling';
    case 'other':
      return 'Other';
  }
}

export function statusLabel(status: ServiceRequestStatus): string {
  switch (status) {
    case 'open':
      return 'Open';
    case 'in_progress':
      return 'In progress';
    case 'resolved':
      return 'Resolved';
    case 'cancelled':
      return 'Cancelled';
  }
}

export function useUpdateServiceRequestStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: ServiceRequestStatus;
    }) => {
      const { error } = await supabase
        .schema('orders')
        .from('service_requests')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: LIST_KEY });
      qc.invalidateQueries({ queryKey: OPEN_KEY });
    },
  });
}
